import React, {useContext, useEffect, useState} from "react";
import styled from "styled-components";
import {Panel} from 'primereact/panel';
import {Inplace, InplaceDisplay, InplaceContent} from 'primereact/inplace';
import {apiRequest, formatDate, parseDataFromAPI, toast} from "@/Helpers/general";
import {Button} from "primereact/button";
import DropdownInputTemplate from "@/Components/Templates/Forms/DropdownInputTemplate";
import {isdFields} from "@/Config/forms/isd";
import {getFormErrorMessage, isFormFieldValid, validateForm} from "@/Helpers/forms";
import TextInputTemplate from "@/Components/Templates/Forms/TextInputTemplate";
import TextareaInputTemplate from "@/Components/Templates/Forms/TextareaInputTemplate";
import DateInputTemplate from "@/Components/Templates/Forms/DateInputTemplate";
import CustomSelectTemplate from "@/Components/Templates/Forms/CustomSelectTemplate";
import {fieldsPlaceholders} from "@/Config/forms";
import NumberInputTemplate from "@/Components/Templates/Forms/NumberInputTemplate";
import {AppContext} from "@/Components/Context/AppContext";
import {Tooltip} from "primereact/tooltip";
import {CaseContext} from "@/Components/Context/CaseContext";
import {RequestsContext} from "@/Components/Context/RequestsContext";
import {InputSwitch} from "primereact/inputswitch";

const IsdWrap = styled.div`
    .isd-wrap {
        min-height: 35vh;
        position: relative;
        padding: 0 0.5rem;

        .p-panel {
            padding: 0 2rem;

            .p-panel-content {
                padding: 0;
                border: 0;
            }

            .p-panel-header {
                border-bottom: 0;
                padding: 0.75rem 1rem;
            }

            .p-panel-title {
                width: 100%;
            }
        }

        .fields-table {
            border-collapse: collapse;

            .edit-field-btn {
                opacity: 0;
            }

            > tbody > tr > td {
                border: 1px solid #DEE2E6;
                padding: 0 1rem;
                vertical-align: middle;

                &:hover {
                    .edit-field-btn {
                        opacity: 1;
                    }
                }

                &.first-column {
                    width: 45%;
                }
            }

            .calendar-icon-wrap {
                top: 17px;
            }
        }

        .p-inplace {
            /* .p-inplace-display {
                cursor: auto;

                &:not(.p-disabled):hover {
                    background: none;
                }
            } */

            .p-inplace-display {
                display: block;
                white-space: pre-wrap;

                .preview-field-value {
                    color: #b5b5b5;
                }

                &:hover {
                    .preview-field-value {
                        color: #495057;
                    }
                }
            }

            .p-inplace-content {
                display: flex;

                > button {
                    margin-left: 1rem;

                    .pi-times::before {
                        content: "\\e909";
                    }
                }
            }
        }
    }
`;

const Isd = ({caseId, formData, setFormData, submitIsd, initialFormData, setInitialFormData, reloadCaseData, request}) => {
    const {toastRef} = useContext(AppContext);
    const {changeCaseTab} = useContext(CaseContext);
    const {chosOptions} = useContext(RequestsContext);
    const [formLoading, setFormLoading] = useState(false);
    const [fieldUpdated, setFieldUpdated] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [formValidationRules, setFormValidationRules] = useState([]);
    const [dropdownsOptions, setDropdownsOptions] = useState({});
    const [activeFields, setActiveFields] = useState({});
    const booleanOptions = [
        {
            label: 'No',
            value: 0
        },
        {
            label: 'Yes',
            value: 1
        }
    ];
    const keysToIgnore = [
        'band_1',
        'band_2',
        'band_3',
        'band_4',
        'case_law_highway_code',
        'claimant_case',
        'coa_case_law',
        'current_date',
        'date_engineer_report_received_by_client',
        'date_repairs_started',
        'date_satisfaction_note_signed',
        'defendant_case',
        'details_of_insured',
        'directions_quantum',
        'duration_arguments',
        'fast',
        'further_investigation',
        'funding',
        'hire_ongoing',
        'impecuniosity',
        'intermediate',
        'intervention',
        'intervention_date',
        'intervention_rate',
        'insured_uninsured_losses',
        'insurer_outlay_recovered',
        'liability',
        'liability_offer_amount',
        'liability_offer_date',
        'mi_checked',
        'moj',
        'moj_position_application_considerations',
        'multi_no_band_needed',
        'need_vehicle_hired',
        'other_interims_applications',
        'other_missing_info_docs',
        'oic_position',
        'pav_paid',
        'proposed_offer_brackets',
        'quantum_recovery_offer',
        'quantum_storage_offer',
        'quantum_uninsured_offer',
        'quantum_vehicle_damage_offer',
        'rate_and_total',
        'repairs_authorised_offer_made',
        'response_hire_daily_rate',
        'response_total',
        'response_total_hire_period',
        'requested_hire_daily_rate',
        'requested_total',
        'requested_total_hire_period',
        'savings_hire_daily_rate',
        'savings_total',
        'savings_total_hire_period',
        'signed_and_dated',
        'strategy_actions',
        'supervisor_comments',
        'valuation_bracket',
    ];

    const onVisibilityChange = async (section, value) => {
        setFormLoading(true)
        await apiRequest('post', 'request/' + request.id + '/toggle-isd-section', {
            data: {
                [section]: value
            },
            onSuccess: (response) => {
                setFormLoading(false);
                reloadCaseData()
                toast(toastRef, 'success', 'View mode successfully changed.');
            },
            onFailure: (error) => {
                setFormLoading(false);
                if (error.status === 403) {
                    toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                } else {
                    toast(toastRef, 'error', 'Error while changing view mode.');
                }
            }
        });
    }

    /**
     * Update the state of the form data (when changing an input value)
     *
     * @param {string} name the name of the input
     * @param {string} value the new value of the input
     */
    const changeFormData = (name, value) => {
        let getFormData = {...formData};

        getFormData['isd'][name] = value;

        setFormData(getFormData);
    };

    /**
     * Generate a preview using the field data (if we have any)
     *
     * @param {object} field object with field data
     */
    const generateFieldPreview = (field) => {
        let defaultValue = <span className="preview-field-value">N/A</span>,
            preview = null;

        switch (field.type) {

            case 'text':
            case 'textarea':
            case 'integer':
            case 'custom-select':
                preview = formData['isd'][field.name] !== '' ? formData['isd'][field.name] : defaultValue;
                break;

            case 'float':
                preview = formData['isd'][field.name] !== '' ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(formData['isd'][field.name]) : defaultValue;
                break;

            case 'date':
                preview = formData['isd'][field.name] ? formatDate(formData['isd'][field.name], true) : defaultValue;
                break;

            case 'dropdown':
                let fieldValue = null;

                if (dropdownsOptions && dropdownsOptions[field.dropdownOptionsAPI] && formData['isd'][field.name]) {
                    let searchValue = dropdownsOptions[field.dropdownOptionsAPI].find(
                        (item) => item.value === formData['isd'][field.name]
                    );

                    if (searchValue) {
                        fieldValue = searchValue.label;
                    }
                }

                preview = fieldValue || defaultValue;
                break;

            case 'boolean':
                if (formData['isd'][field.name] === true || formData['isd'][field.name] === 1) {
                    preview = 'Yes';
                }
                if (formData['isd'][field.name] === false || formData['isd'][field.name] === 0) {
                    preview = 'No';
                }
                break;

            default:
                break;
        }

        return preview !== null ? preview : defaultValue;
    };

    /**
     * Generate an input based on the field type
     *
     * @param {object} field object with field data
     */
    const generateInputField = (field) => {
        switch (field.type) {
            case 'text':
                return (
                    <TextInputTemplate
                        name={field.name}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                        value={formData['isd'][field.name] || ''}
                        noMargins
                        focusInput
                        selectInput
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        labelClassName="hidden"
                        inline
                        className="my-2"
                    />
                );

            case 'textarea':
                return (
                    <TextareaInputTemplate
                        name={field.name}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.text}
                        value={formData['isd'][field.name] || ''}
                        noMargins
                        focusInput
                        selectInput
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        labelClassName="hidden"
                        inline
                        className="my-2"
                    />
                );

            case 'date':
                return (
                    <DateInputTemplate
                        name={field.name}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.date}
                        value={formData['isd'][field.name] || ''}
                        noMargins
                        focusInput
                        selectInput
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        labelClassName="hidden"
                        inline
                        className="my-2"
                    />
                );

            case 'boolean':
            case 'dropdown':
                return (
                    <DropdownInputTemplate
                        name={field.name}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.boolean}
                        value={formData['isd'][field.name] || formData['isd'][field.name] === 0 ? formData['isd'][field.name] : ''}
                        options={field.type == 'dropdown' ? (dropdownsOptions && dropdownsOptions[field.dropdownOptionsAPI] ? dropdownsOptions[field.dropdownOptionsAPI] : []) : booleanOptions}
                        noMargins
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        labelClassName="hidden"
                        inline
                        className="my-2"
                    />
                );

            case 'integer':
                return (
                    <NumberInputTemplate
                        name={field.name}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                        value={formData['isd'][field.name] || ''}
                        noMargins
                        focusInput
                        selectInput
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        labelClassName="hidden"
                        inline
                        className="my-2"
                    />
                );

            case 'float':
                return (
                    <NumberInputTemplate
                        name={field.name}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.integer}
                        value={typeof formData['isd'][field.name] !== undefined ? formData['isd'][field.name] : null}
                        mode={'decimal'}
                        noMargins
                        focusInput
                        selectInput
                        format
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        labelClassName="hidden"
                        inline
                        className="my-2"
                    />
                );

            case 'custom-select':
                return (
                    <CustomSelectTemplate
                        name={field.name}
                        placeholder={field.custom_placeholder || fieldsPlaceholders.boolean}
                        value={formData['isd'][field.name] || ''}
                        options={field.customOptions}
                        noMargins
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, field.name)}
                        errorMessage={getFormErrorMessage(formErrors, field.name)}
                        labelClassName="hidden"
                        inline
                        className="my-2"
                    />
                );

            default:
                break;
        }
    };

    /**
     * Save the current tab data
     */
    const submitForm = async () => {
        setFormLoading(true);

        let validation = validateForm(formValidationRules, formData, setFormErrors);

        if (validation) {
            apiRequest('post', 'case/' + caseId + '/isd', {
                data: formData['isd'],
                onSuccess: (response) => {
                    setFormLoading(false);
                    changeCaseTab('documents');

                    toast(toastRef, 'success', '"Initial Strategy Document" pdf file has been generated and can be downloaded from the Documents tab.');
                },
                onFailure: (error) => {
                    setFormLoading(false);

                    if (error.status == 422) {
                        if (error.messages) {
                            let errorMessages = error.messages,
                                newFormErrors = {};

                            for (let key in errorMessages) {
                                if (Object.hasOwnProperty.call(errorMessages, key)) {
                                    newFormErrors[key] = errorMessages[key];
                                }
                            }

                            setFormErrors(newFormErrors);
                        } else {
                            toast(toastRef, 'error', 'Error generating "Initial Strategy Document" pdf file.');
                        }

                    } else if (error.status == 403) {
                        toast(toastRef, 'error', 'Error: the case needs to be assigned in order to save data.');

                    } else {
                        toast(toastRef, 'error', 'Error generating "Initial Strategy Document" pdf file.');
                    }
                }
            });
        } else {
            setFormLoading(false);
        }
    };

    const updateActiveFields = (fieldName, value) => {
        let copyActiveFields = {...activeFields};
        // let copyActiveFields = {};

        copyActiveFields[fieldName] = value;

        setActiveFields(copyActiveFields);
    };

    function filterEntries(object, keysToFilter) {
        const filteredTabData = {};

        for (const [key, value] of Object.entries(object)) {
            if (!keysToFilter.includes(key)) {
                filteredTabData[key] = value;
            }
        }

        return filteredTabData;
    }

    /**
     * Get documents from the API
     */
    const loadIsdFields = async () => {
        setFormLoading(true);

        apiRequest('get', 'case/' + caseId + '/isd', {
            data: [],
            onSuccess: (response) => {
                if (response.result && Object.keys(response.result).length > 0) {
                    let getFormData = { ...formData },
                        getInitialFormData = { ...initialFormData };

                    let nonNullValues = Object.fromEntries(
                        Object.entries(response.result).filter(([key, value]) => value !== null)
                    );

                    getInitialFormData['isd'] = filterEntries(getInitialFormData['isd'], keysToIgnore);

                    let parsedValues = parseDataFromAPI(nonNullValues,'isd');

                    Object.entries(parsedValues).forEach(([key, value]) => {
                        getInitialFormData['isd'][key] = value;
                        getFormData['isd'][key] = value;
                    });

                    setInitialFormData(getInitialFormData);
                    setFormData(getFormData);
                }
                setFormLoading(false);
            },
            onFailure: () => {
                setFormLoading(false);
            },
        });
    };

    /**
     * Initialize the state for each tab
     */
    const initStateData = () => {
        let initFormValidationRules = [],
            initActiveFields = {};
        isdFields.forEach((category) => {
            category.fields.forEach((field) => {
                initFormValidationRules.push({
                    field: field.name,
                    // rules: field.modalValidation || []
                    rules: []
                });
                initActiveFields[field.name] = false;
            });
        });

        setFormValidationRules(initFormValidationRules);
        setActiveFields(initActiveFields);
    };

    useEffect(() => {
        if (chosOptions) {
            isdFields.forEach((category) => {
                category.fields.forEach((field) => {
                    // set dropdown options from the API
                    if (field?.dropdownOptionsAPI === 'chos') {
                        setDropdownsOptions((previousState) => ({
                            ...previousState, chos: chosOptions
                        }))
                    }
                });
            });
        }
    }, [chosOptions]);

    useEffect(() => {
        loadIsdFields()
        initStateData();
    }, []);

    useEffect(() => {
        if (fieldUpdated) {
            loadIsdFields();
        }
        setFieldUpdated(false);
    }, [formData]);


    const panelHeader = (category) => {
        return <div className='flex justify-content-between'>
            <span className='align-self-center'>{category.name}</span>
            {category.can_be_disabled && <div>
                <div className='flex justify-content-center align-items-center gap-2'>
                    <span className='font-semibold'>Hide</span>
                    <InputSwitch
                        id={`${category.key}-switch-view-mode`}
                        checked={request[`isd_${category.key}_visible`]}
                        onChange={(e) => {
                            onVisibilityChange(`isd_${category.key}_visible`, e.value)
                        }}
                    />
                    <span className='font-semibold'>Show</span>
                </div>
            </div>}
        </div>
    }

    return (
        <IsdWrap>
            <div className="custom-content">
                <div className="isd-wrap">
                    {isdFields.map((category) => {
                        return (
                            <Panel header={panelHeader(category)} className="mb-5" key={category.name}>
                                <table className="fields-table w-full">
                                    <tbody>
                                    {category.fields.map((field) => {
                                        if ((category.key != 'lou_dates' && !field.checkValue)
                                            || (category.key == 'lou_dates' && formData['isd'][field.name])
                                            || (field.checkValue && formData['isd'][field.name])) {
                                            return (
                                                <tr key={field.name}>
                                                    <td className="first-column">{field.label}</td>
                                                    <td>
                                                        <div className="flex align-items-center">
                                                            <Inplace active={activeFields[field.name]} disabled={field.disabled} onToggle={(e) => updateActiveFields(field.name, e.value)}>
                                                                <InplaceDisplay id={'field-preview-' + field.name}>
                                                                    {generateFieldPreview(field)}
                                                                    <Tooltip target={'#field-preview-' + field.name} content="Click to edit" position="top"/>
                                                                </InplaceDisplay>

                                                                <InplaceContent>
                                                                    {generateInputField(field)}
                                                                    <div className="m-2">
                                                                        <Button
                                                                            className="p-button-icon-only"
                                                                            icon="pi pi-check"
                                                                            onClick={() => {
                                                                                submitIsd();
                                                                                if (!keysToIgnore.includes(field.name)) {
                                                                                    setFieldUpdated(true);
                                                                                }
                                                                                updateActiveFields(field.name, false);
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </InplaceContent>
                                                            </Inplace>

                                                            {/* {!activeFields[field.name] && <Button
                                                                    className="edit-field-btn p-button-icon-only grey-nav-btn grey-nav-btn-hover p-button-sm"
                                                                    icon="pi pi-pencil"
                                                                    onClick={() => updateActiveFields(field.name, true)}
                                                                />} */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    })}
                                    </tbody>
                                </table>
                            </Panel>
                        );
                    })}

                    <hr/>
                    <div className="text-right">
                        <Button label="Generate Strategy Document" onClick={submitForm} loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center"/>
                    </div>
                </div>
            </div>
        </IsdWrap>
    );
};

export default Isd;
